import {
    EventPackage,
    EventPackagePriceGroup,
    SectionSeat,
} from "./AssignedSeating";
import { SalesContext } from "./SalesContext";
import { SeatmapDecoration } from "./SeatmapDecoration";
import { SimpleUser } from "./User";

export enum DepartmentVisibility {
    PROHIBITED = "PROHIBITED",
    VISIBLE_INACTIVE = "VISIBLE_INACTIVE",
    HIDDEN_ACTIVE = "HIDDEN_ACTIVE",
    OPEN = "OPEN",
}

export enum EventListType {
    DEFAULT = "DEFAULT",
    DATE_SESSION_PICKER = "DATE_SESSION_PICKER",
    DATE_PICKER = "DATE_PICKER",
    COMING_SOON = "COMING_SOON",
    CUSTOM = "CUSTOM",
    DINNER_EVENT = "DINNER_EVENT",
    VIP_CONCERT = "VIP_CONCERT",
}

export type EventListStyle = {
    background?: string;
    calendarBorder?: string;
    calendarBackground?: string;
    dateBackgroundColor?: string;
    fontName?: string;
    logo?: string;
    dialogGradient?: string;
    primaryColor?: string;
    primaryColorDark?: string;
    primaryContrastText?: string;
};

export interface EventList {
    id: number;
    type: EventListType;
    style: EventListStyle;
    config: EventListConfig;
}

export type YearMonth = {
    year: number;
    month: number;
};

export type CalendarDay = {
    year: number;
    month: number;
    day: number;
};

export enum CalendarSessionType {
    TEN = "TEN",
    TEN_FIFTEEN = "TEN_FIFTEEN",
    TEN_THIRTY = "TEN_THIRTY",
    TEN_FORTY_FIVE = "TEN_FORTY_FIVE",
    ELEVEN = "ELEVEN",
    ELEVEN_FIFTEEN = "ELEVEN_FIFTEEN",
    ELEVEN_THIRTY = "ELEVEN_THIRTY",
    ELEVEN_FORTY_FIVE = "ELEVEN_FORTY_FIVE",
    TWELVE = "TWELVE",
    TWELVE_FIFTEEN = "TWELVE_FIFTEEN",
    TWELVE_THIRTY = "TWELVE_THIRTY",
    TWELVE_FORTY_FIVE = "TWELVE_FORTY_FIVE",
    ONE = "ONE",
    ONE_FIFTEEN = "ONE_FIFTEEN",
    ONE_THIRTY = "ONE_THIRTY",
    ONE_FORTY_FIVE = "ONE_FORTY_FIVE",
    TWO = "TWO",
    TWO_FIFTEEN = "TWO_FIFTEEN",
    TWO_THIRTY = "TWO_THIRTY",
    TWO_FORTY_FIVE = "TWO_FORTY_FIVE",
    THREE = "THREE",
    THREE_FIFTEEN = "THREE_FIFTEEN",
    THREE_THIRTY = "THREE_THIRTY",
    THREE_FORTY_FIVE = "THREE_FORTY_FIVE",
    FOUR = "FOUR",
    FOUR_FIFTEEN = "FOUR_FIFTEEN",
    FOUR_THIRTY = "FOUR_THIRTY",
    FOUR_FORTY_FIVE = "FOUR_FORTY_FIVE",
    FIVE = "FIVE",
    FIVE_FIFTEEN = "FIVE_FIFTEEN",
    FIVE_THIRTY = "FIVE_THIRTY",
    FIVE_FORTY_FIVE = "FIVE_FORTY_FIVE",
    SIX = "SIX",
    SIX_FIFTEEN = "SIX_FIFTEEN",
    SIX_THIRTY = "SIX_THIRTY",
    SIX_FORTY_FIVE = "SIX_FORTY_FIVE",
    SEVEN = "SEVEN",
    SEVEN_FIFTEEN = "SEVEN_FIFTEEN",
    SEVEN_THIRTY = "SEVEN_THIRTY",
    SEVEN_FORTY_FIVE = "SEVEN_FORTY_FIVE",
    EIGHT = "EIGHT",
    EIGHT_FIFTEEN = "EIGHT_FIFTEEN",
    EIGHT_THIRTY = "EIGHT_THIRTY",
    EIGHT_FORTY_FIVE = "EIGHT_FORTY_FIVE",
    NINE = "NINE",
    FLEX = "FLEX",
    VIP = "VIP",
    WEEKDAY_AFTERNOON = "WEEKDAY_AFTERNOON",
    WEEKDAY_EVENING = "WEEKDAY_EVENING",
    WEEKEND_AFTERNOON = "WEEKEND_AFTERNOON",
    WEEKEND_EVENING = "WEEKEND_EVENING",
    CS_EVENING = "CS_EVENING",
    TWO_PM_TO_FOUR_PM = "TWO_PM_TO_FOUR_PM",
    TWO_PM_TO_SIX_THIRTY_PM = "TWO_PM_TO_SIX_THIRTY_PM",
    FOUR_PM_TO_SIX_THIRTY_PM = "FOUR_PM_TO_SIX_THIRTY_PM",
    SIX_THIRTY_PM_TO_NINE_PM = "SIX_THIRTY_PM_TO_NINE_PM",
    ONE_PM_TO_FOUR_THIRTY_PM = "ONE_PM_TO_FOUR_THIRTY_PM",
    FOUR_THIRTY_PM_TO_NINE_PM = "FOUR_THIRTY_PM_TO_NINE_PM",
}

const sessionTypeToWeight = {
    [CalendarSessionType.VIP]: 0,
    [CalendarSessionType.TEN]: 1,
    [CalendarSessionType.TEN_FIFTEEN]: 2,
    [CalendarSessionType.TEN_THIRTY]: 3,
    [CalendarSessionType.TEN_FORTY_FIVE]: 4,
    [CalendarSessionType.ELEVEN]: 5,
    [CalendarSessionType.ELEVEN_FIFTEEN]: 6,
    [CalendarSessionType.ELEVEN_THIRTY]: 7,
    [CalendarSessionType.ELEVEN_FORTY_FIVE]: 8,
    [CalendarSessionType.TWELVE]: 9,
    [CalendarSessionType.TWELVE_FIFTEEN]: 10,
    [CalendarSessionType.TWELVE_THIRTY]: 11,
    [CalendarSessionType.TWELVE_FORTY_FIVE]: 12,
    [CalendarSessionType.ONE]: 13,
    [CalendarSessionType.ONE_FIFTEEN]: 14,
    [CalendarSessionType.ONE_THIRTY]: 15,
    [CalendarSessionType.ONE_FORTY_FIVE]: 16,
    [CalendarSessionType.TWO]: 17,
    [CalendarSessionType.TWO_FIFTEEN]: 18,
    [CalendarSessionType.TWO_THIRTY]: 19,
    [CalendarSessionType.TWO_FORTY_FIVE]: 20,
    [CalendarSessionType.THREE]: 21,
    [CalendarSessionType.THREE_FIFTEEN]: 22,
    [CalendarSessionType.THREE_THIRTY]: 23,
    [CalendarSessionType.THREE_FORTY_FIVE]: 24,
    [CalendarSessionType.FOUR]: 25,
    [CalendarSessionType.FOUR_FIFTEEN]: 26,
    [CalendarSessionType.FOUR_THIRTY]: 27,
    [CalendarSessionType.FOUR_FORTY_FIVE]: 28,
    [CalendarSessionType.FIVE]: 29,
    [CalendarSessionType.FIVE_FIFTEEN]: 30,
    [CalendarSessionType.FIVE_THIRTY]: 31,
    [CalendarSessionType.FIVE_FORTY_FIVE]: 32,
    [CalendarSessionType.SIX]: 33,
    [CalendarSessionType.SIX_FIFTEEN]: 34,
    [CalendarSessionType.SIX_THIRTY]: 35,
    [CalendarSessionType.SIX_FORTY_FIVE]: 36,
    [CalendarSessionType.SEVEN]: 37,
    [CalendarSessionType.SEVEN_FIFTEEN]: 38,
    [CalendarSessionType.SEVEN_THIRTY]: 39,
    [CalendarSessionType.SEVEN_FORTY_FIVE]: 40,
    [CalendarSessionType.EIGHT]: 41,
    [CalendarSessionType.EIGHT_FIFTEEN]: 42,
    [CalendarSessionType.EIGHT_THIRTY]: 43,
    [CalendarSessionType.EIGHT_FORTY_FIVE]: 44,
    [CalendarSessionType.NINE]: 45,
    [CalendarSessionType.FLEX]: 46,
    [CalendarSessionType.WEEKDAY_AFTERNOON]: 49,
    [CalendarSessionType.WEEKDAY_EVENING]: 50,
    [CalendarSessionType.WEEKEND_AFTERNOON]: 51,
    [CalendarSessionType.WEEKEND_EVENING]: 52,
    [CalendarSessionType.CS_EVENING]: 53,
    [CalendarSessionType.TWO_PM_TO_FOUR_PM]: 54,
    [CalendarSessionType.TWO_PM_TO_SIX_THIRTY_PM]: 55,
    [CalendarSessionType.FOUR_PM_TO_SIX_THIRTY_PM]: 55,
    [CalendarSessionType.SIX_THIRTY_PM_TO_NINE_PM]: 56,
    [CalendarSessionType.ONE_PM_TO_FOUR_THIRTY_PM]: 57,
    [CalendarSessionType.FOUR_THIRTY_PM_TO_NINE_PM]: 58,
};

export const sessionTypeToTime = {
    [CalendarSessionType.TEN]: "10am",
    [CalendarSessionType.TEN_FIFTEEN]: "10:15am",
    [CalendarSessionType.TEN_THIRTY]: "10:30am",
    [CalendarSessionType.TEN_FORTY_FIVE]: "10:45am",
    [CalendarSessionType.ELEVEN]: "11am",
    [CalendarSessionType.ELEVEN_FIFTEEN]: "11:15am",
    [CalendarSessionType.ELEVEN_THIRTY]: "11:30am",
    [CalendarSessionType.ELEVEN_FORTY_FIVE]: "11:45am",
    [CalendarSessionType.TWELVE]: "12pm",
    [CalendarSessionType.TWELVE_FIFTEEN]: "12:15pm",
    [CalendarSessionType.TWELVE_THIRTY]: "12:30pm",
    [CalendarSessionType.TWELVE_FORTY_FIVE]: "12:45pm",
    [CalendarSessionType.ONE]: "1pm",
    [CalendarSessionType.ONE_FIFTEEN]: "1:15pm",
    [CalendarSessionType.ONE_THIRTY]: "1:30pm",
    [CalendarSessionType.ONE_FORTY_FIVE]: "1:45pm",
    [CalendarSessionType.TWO]: "2pm",
    [CalendarSessionType.TWO_FIFTEEN]: "2:15pm",
    [CalendarSessionType.TWO_THIRTY]: "2:30pm",
    [CalendarSessionType.TWO_FORTY_FIVE]: "2:45pm",
    [CalendarSessionType.THREE]: "3pm",
    [CalendarSessionType.THREE_FIFTEEN]: "3:15pm",
    [CalendarSessionType.THREE_THIRTY]: "3:30pm",
    [CalendarSessionType.THREE_FORTY_FIVE]: "3:45pm",
    [CalendarSessionType.FOUR]: "4pm",
    [CalendarSessionType.FOUR_FIFTEEN]: "4:15pm",
    [CalendarSessionType.FOUR_THIRTY]: "4:30pm",
    [CalendarSessionType.FOUR_FORTY_FIVE]: "4:45pm",
    [CalendarSessionType.FIVE]: "5pm",
    [CalendarSessionType.FIVE_FIFTEEN]: "5:15pm",
    [CalendarSessionType.FIVE_THIRTY]: "5:30pm",
    [CalendarSessionType.FIVE_FORTY_FIVE]: "5:45pm",
    [CalendarSessionType.SIX]: "6pm",
    [CalendarSessionType.SIX_FIFTEEN]: "6:15pm",
    [CalendarSessionType.SIX_THIRTY]: "6:30pm",
    [CalendarSessionType.SIX_FORTY_FIVE]: "6:45pm",
    [CalendarSessionType.SEVEN]: "7pm",
    [CalendarSessionType.SEVEN_FIFTEEN]: "7:15pm",
    [CalendarSessionType.SEVEN_THIRTY]: "7:30pm",
    [CalendarSessionType.SEVEN_FORTY_FIVE]: "7:45pm",
    [CalendarSessionType.EIGHT]: "8pm",
    [CalendarSessionType.EIGHT_FIFTEEN]: "8:15pm",
    [CalendarSessionType.EIGHT_THIRTY]: "8:30pm",
    [CalendarSessionType.EIGHT_FORTY_FIVE]: "8:45pm",
    [CalendarSessionType.NINE]: "9pm",
    [CalendarSessionType.FLEX]: "FLEX",
    [CalendarSessionType.VIP]: "VIP",
    [CalendarSessionType.WEEKDAY_AFTERNOON]: "Afternoon 10am-3pm",
    [CalendarSessionType.WEEKDAY_EVENING]: "Evening 3pm-8pm",
    [CalendarSessionType.WEEKEND_AFTERNOON]: "Afternoon 10am-3pm",
    [CalendarSessionType.WEEKEND_EVENING]: "Evening 3pm-8pm",
    [CalendarSessionType.CS_EVENING]: "10:30pm",
    [CalendarSessionType.TWO_PM_TO_FOUR_PM]: "2pm-4pm",
    [CalendarSessionType.TWO_PM_TO_SIX_THIRTY_PM]: "2pm-6:30pm",
    [CalendarSessionType.FOUR_PM_TO_SIX_THIRTY_PM]: "4pm-6:30pm",
    [CalendarSessionType.SIX_THIRTY_PM_TO_NINE_PM]: "6:30pm-9pm",
    [CalendarSessionType.ONE_PM_TO_FOUR_THIRTY_PM]: "1pm-4:30pm",
    [CalendarSessionType.FOUR_THIRTY_PM_TO_NINE_PM]: "4:30pm-9pm",
};

export const sortSessionTypes = (sessionTypes: CalendarSessionType[]) => {
    return sessionTypes.sort((a, b) => {
        return sessionTypeToWeight[a] - sessionTypeToWeight[b];
    });
};

export interface CalendarConfig {
    months: YearMonth[];
    faqs?: {
        question: string;
        answer: string;
    }[];
    faqLink?: string;
    priceButtons?: {
        name: string;
        price: string;
        description?: string;
        from?: boolean;
    }[];
    premiumTickets?: {
        departmentId: number;
    }[];
    premiumTicketDisclaimer?: string;
    showLandingPage?: boolean;
    disclaimer?: string;
    viewTicketButtonName?: string;
    priceButtonDisclaimer?: string;
    calendarDisclaimer?: string;
    sessionPicker?: "SELECT" | "BUTTON" | "NONE";
    legend?: {
        title: string;
        dates: {
            date: string;
            event: string;
        }[];
    };
    priceGuide?: {
        categories: {
            key: string;
            label: string;
            color: string;
            description?: string;
            maxPrice?: number; // Optional max price threshold in cents for automatic categorization
        }[];
    };
    customTemplate?: CustomComponent;
    // Gate admission upsell for flows like VIP_CONCERT where the ticket does
    // not include grounds admission. When present, the flow will offer one
    // gate ticket per unique selection date. Any cart item whose event.id
    // matches `eventId` counts as a gate ticket; the first department under
    // that event is used when adding.
    gateAdmission?: {
        eventId: number;
    };
    // Soft password gate applied to the event list on the store route.
    // Client-side only: the password is sent to the browser, so this is
    // for soft-launches / private previews, not real access control.
    password?: string;
}

export type CustomComponent = {
    type:
        | "Box"
        | "Grid"
        | "GridItem"
        | "Button"
        | "Text"
        | "Image"
        | "RawHTML"
        | "AddDepartmentToCartButton"
        | "RemoveDepartmentFromCartButton";
    props: any;
    children?: CustomComponent[] | string;
};

export type EventListConfig = CalendarConfig;

export interface DepartmentCategory {
    id: number;
    name: string;
    weight: number;
}

export interface SimpleEvent {
    id: number;
    client: Client;
    title: string;
    boxofficeTitle: string;
    boxofficeTicketDisclaimer: string;
    from: number;
    to: number;
    eventFrom: number;
    eventTo: number;
    showEventDates: boolean;
    venueConfiguration: VenueConfiguration;
    requiredEvent: SimpleEvent | null;
    assignedSeating: boolean;
    logoImage?: string;
    eventListImage?: string;
    usesPosPostalCode: boolean;
    weight: number | null;
    eventList?: EventList;

    // At the moment, all departments in an event must have the same "taxability".
    // When the legacy ecommerce interface is deprecated, we can get away from that
    // and instead use the applicable tax feature on a line-by-line level.
    // Until then, this logic will be applied/calculated on the back-end.
    taxDisabled: boolean;
}

export enum EventPublicVisibility {
    BOXOFFICE_ONLY = "BOXOFFICE_ONLY",
    DIRECT_LINK_ONLY = "DIRECT_LINK_ONLY",
    PUBLIC = "PUBLIC",
}

export interface Event extends SimpleEvent {
    details: string;
    viewTicketsGraphic: string;
    departments: Array<Department>;
    seatmaps: Array<SimpleEventSeatmap>;
    packages: Array<EventPackage>;
    compositeDepartments: Array<CompositeDepartment>;

    publicVisibility?: EventPublicVisibility;
    isIndexable?: boolean;

    /** @deprecated Replaced by publicVisibility. Retained for one release. */
    boxofficeOnly: boolean;
}

export interface SimpleEventSeatmap {
    id: number;
    name: string;
    weight: number | null;
    description: string;
    extendedDetails: string | null;
    startDate: number;
    endDate: number;
    ecommerceStartDate?: number | null;
    ecommerceEndDate?: number | null;
    boxofficeStartDate?: number | null;
    boxofficeEndDate?: number | null;
    posStartDate?: number | null;
    posEndDate?: number | null;
    accessibleDepartmentId: number | null;
    ecommerceVisibility: DepartmentVisibility;
    boxofficeVisibility: DepartmentVisibility;
    posVisibility: DepartmentVisibility;
    category: DepartmentCategory | null;
}

export interface EventSeatmap extends SimpleEventSeatmap {
    additionalSvg: string | null;
    seatmapTemplate: SeatmapTemplate;
    priceGroups: EventSeatmapPriceGroup[];
}

export interface SimpleSeatmapTemplate {
    id: number;
    clientId: number;
    name: string;
    /**
     * Generated by admin-api: the hand-authored `backgroundSvgSource` with the decorations
     * serialized on top. Still rendered as-is when a template predates structured decorations.
     */
    backgroundSvg: string | null;
    backgroundSvgSource?: string | null;
    decorations?: SeatmapDecoration[];
}

export interface OptimalPoint {
    x: number; // Absolute coordinates in viewbox space
    y: number;
    weight: number; // 1-100 for multiple point importance
    label?: string; // "Center Stage", "50-yard line", etc.
    maxDistance?: number; // Optional distance cutoff
}

export interface SeatmapTemplate extends SimpleSeatmapTemplate {
    sections: SeatmapSection[];
    viewboxX: number | null;
    viewboxY: number | null;
    viewboxWidth: number | null;
    viewboxHeight: number | null;
    optimalPoints?: OptimalPoint[];
}

export interface SimpleSeatmapSection {
    id: number;
    name: string;
    sectionDetails: string;
}

export enum SeatAlignment {
    DEFAULT = "DEFAULT",
    CENTER = "CENTER",
    CENTER_CONE = "CENTER_CONE",
    LEFT = "LEFT",
    RIGHT = "RIGHT",
    SKEW_VERTICAL = "SKEW_VERTICAL",
    SKEW_HORIZONTAL = "SKEW_HORIZONTAL",
}

export interface SeatmapSection extends SimpleSeatmapSection {
    idx: number;
    type: string;
    fontSize: number;
    fontColor: string;
    message: string;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    seatSpacing: number;
    rowSpacing: number;
    seatCurve: number;
    seatRadius: number;
    borderRadius: number;
    fill: string;
    seatAlignment: SeatAlignment;
    labelAlignment: string;
    rowLabelAlignment: string;
    rotation: number;
    skew: number;
    svg: string | null;
    typeName: string | null;
    isBoxofficeOnly: boolean;
    requireEntireSection: boolean;
    config: any | null;
    parentSections: ParentSectionType[];
    sections: SeatmapSection[];
    seats: SectionSeat[];
}

export interface ParentSectionType {
    type: string;
    name: string;
}

export interface EventSeatmapPriceGroup {
    id: number;
    name: string;
    color: string;

    seats: number[];
    sections: { sectionId: number; quantity: number | null }[];
    departments: SimpleDepartment[];
}

export interface SectionSeatStatus {
    seatId: number;
    seatStatus: boolean;
    packageStatus: boolean;
    seatHoldGroupStatus: boolean;
    packageHoldGroupStatus: boolean;
}

export interface SeatmapSectionStatus {
    sectionId: number;
    /** Configured GA capacity across price groups; null = unlimited. */
    capacity: number | null;
    /** Tickets consumed (in cart, reserved, or purchased). */
    sold: number;
    /** Tickets left, or null when capacity is unlimited. */
    remaining: number | null;
    soldOut: boolean;
}

export interface ClientStyle {
    header1Color: string;
    btnColor1: string;
    btnColor2: string;
    backgroundColor?: string;
    eventListBackgroundColor?: string;
}

export interface ClientDonation {
    id: number;
    name: string;
    allowCustomAmount: boolean;
    config: {
        amounts: number[];
        imageUrl?: string;
        description?: string;
    };
    eventIds: number[];
    departmentIds: number[];
}

export interface EcommerceEventList {
    events: Array<Event>;
    cartUrl?: string;
    clientName: string;
    clientId: number;
    deliveryMethods: Array<DeliveryMethod>;
    allowCustomAmount: boolean;
    customFields: Array<CustomField>;
    donations: Array<ClientDonation>;
    gtmContainerId: string | null;
    customerServiceInfo: string;
    termsAndConditions: string | null;
    logoUrl: string;
    styleMap: ClientStyle | null;
    privacyUrl: string | null;
    landingPageHtml: string | null;
    partnerName: string | null;
    newStyleMap: any | null;
}

export interface Contact {
    id: number;
    firstName: string;
    lastName: string;
}

export interface Partner {
    id: number;
    corporationName: string;
    contact: Contact;
}

export interface LandingPage {
    id: number;
    name: string;
}

export interface HoldGroup {
    id: number | null;
    name: string;
    description: string;
    isArchived: boolean;
    eventPackages: Array<HoldGroupEventPackage>;
    seats: Array<HoldGroupSeat>;
    sections: Array<HoldGroupSection>;
    specialDepartmentAccess: {
        id: number;
        landingPage: LandingPage;
        partner: Partner;
    } | null;
}

export interface HoldGroupEventPackage {
    id: number | null;
    seat: SectionSeat | null;
    section: SimpleSeatmapSection | null;
    quantity: number | null;
    soldQuantity: number | null;
    eventPackage: EventPackage;
    overridePrice: number | null;
    preferredPriceGroup: EventPackagePriceGroup | null;
    // Present on entries surfaced via the reserved-tickets endpoint; lets the
    // frontend route a cart-add through the right hold group.
    holdGroupId?: number;
}

export interface HoldGroupSeat {
    id: number | null;
    overridePrice: number | null;
    seat: SectionSeat | null;
    quantity: number | null;
    soldQuantity: number | null;
    eventSeatmap: SimpleEventSeatmap;
    priceGroups: SimpleDepartment[];
}

export interface HoldGroupSection {
    id: number | null;
    overridePrice: number | null;
    quantity: number | null;
    soldQuantity: number | null;
    section: SimpleSeatmapSection;
    eventSeatmap: SimpleEventSeatmap;
    department: SimpleDepartment | null;
    priceGroups: SimpleDepartment[];
}

export interface ReservedTicketList {
    packages: Array<HoldGroupEventPackage>;
    seats: Array<HoldGroupSeat>;
    sections: Array<HoldGroupSection>;
    seatmaps: Array<EventSeatmap>;
    events: Array<Event>;
    cartUrl?: string;
    clientName: string;
    clientId: number;
    emsClientIds: number[];
    deliveryMethods: Array<DeliveryMethod>;
    customFields: Array<CustomField>;
    gtmContainerId: string | null;
    customerServiceInfo: string;
    termsAndConditions: string | null;
    logoUrl: string;
    styleMap: ClientStyle | null;
    privacyUrl: string | null;
    landingPageHtml: string | null;
    partnerName: string | null;
    newStyleMap: any | null;
    landingPageId: number | null;
}

export interface EcommerceEvent {
    event: Event | null;
    cartUrl?: string;
    clientName: string;
    clientId: number;
    deliveryMethods: Array<DeliveryMethod>;
    gtmContainerId: string | null;
    customerServiceInfo: string;
    logoUrl: string;
    styleMap: ClientStyle | null;
    privacyUrl: string | null;
    landingPageHtml: string | null;
    partnerName: string | null;
    newStyleMap: any | null;
}

export enum DiscountType {
    PERCENTAGE = "PERCENTAGE",
    DOLLAR = "DOLLAR",
    TIERED = "TIERED",
}

export interface DiscountAmount {
    id: number;
    minQuantity: number;
    maxQuantity: number;
    value: number;
    type: DiscountType;
    uses: number;
}

export interface Discount {
    id: number;
    name: string;
    code: string;
    type: DiscountType;
    value: number;
    eventId: number;
    uses: number;
    minItems: number;
    maxUses: number | null;
    amounts: DiscountAmount[];
    departmentIds: number[];
}

export interface SimpleDepartment {
    id: number;
    name: string;
    mobileTicketName: string | null;
    price: number;
    // Server-computed pre-tax price: equals price when tax-exclusive, or price
    // with the included tax backed out when tax-inclusive
    basePrice?: number;
    // When true, the advertised price already includes tax (the server backs
    // the tax out at checkout so the buyer pays exactly this price)
    taxInclusive?: boolean;
    // Server-computed per-tax breakdown of the advertised price (same shape as
    // cart item taxes): backed out of the price when inclusive, added when
    // exclusive. Empty when the event is tax-disabled or no taxes apply.
    taxes?: Array<{
        taxId: number;
        name: string;
        rate: number;
        amount: number;
    }>;
    quantity: number;
    from: number;
    to: number;
    ecommerceFrom?: number | null;
    ecommerceTo?: number | null;
    boxofficeFrom?: number | null;
    boxofficeTo?: number | null;
    posFrom?: number | null;
    posTo?: number | null;
    performanceDate: number | null;
    orderWeight: number | null;
    admitCount: number;
    assignedSeating: boolean;
    serviceFees: Array<DepartmentServiceFee>;
    // NOTE: This applies to "ecommerce" context only.
    //       If true, the back-end will prevent the number of tickets for this
    //       department from exceeding the specified number.
    //       This will be enforced server-side.
    maxTicketsInCart: number;
    soldOut: boolean;
    maxQuantity: number | null;
    soldQuantity: number | null;
    limitedTicketsAvailable: boolean;

    // POS Specific
    generatesTicket: boolean;
    usesShippingAddress: boolean;
    couponImage?: string;
    timeRestrictions: Array<DepartmentTimeRestriction>;
    posNumbersAllowed: Array<number> | null;
    tokenImage?: string;
    tokenCount?: number;

    metadata?: { [key: string]: any } & {
        year?: number;
        month?: number;
        day?: number;
        session?: CalendarSessionType;
        tags?: {
            color: string;
            label: string;
        }[];
        priceCategory?: string; // For explicit price category assignment
        specialEventName?: string; // Name of special event (e.g., "Santa Visit", "Senior's Day")
        eventType?: string; // Type of event (e.g., "special", "vip")
        sessionTime?: string; // Custom session time display
        sessionName?: string; // Custom session name
        displayName?: string; // Custom display name for department
        displayPrice?: string; // Custom price display
        typeFriendly?: string; // Friendly type name
    };
    customFields: {
        id: number;
        name: string;
        internalName: string;
        type: string;
        required: boolean;
        options: {
            id: number;
            label: string;
            value: string;
        }[];
    }[];

    mobileTicketTemplate: {
        backgroundColour: string | null;
        accentColour: string | null;
        ticketLogo: string | null;
        clientLogo: string | null;
        mobileDescription: string | null;
    } | null;

    // Application properties
    discount: Discount | null;

    // Only on POS
    hasTax?: boolean;
}

export interface Department extends SimpleDepartment {
    details: string;
    extendedDetails: string | null;
    clickImage: string;
    posChicletColour: string;

    // NOTE: If true, this department will not be listed on its parent event page,
    //       and the back-end will prevent any of its departments from being
    //       added to cart unless the user is in a POS or box-office context.
    // KB Aug 27, 2018: Deprecated. We will instead filter departments based on sales context in the future.
    boxofficeOnly: boolean;
    // NOTE: This applies to "ecommerce" context only (POS and box-office will still have access).*
    //       If true, this will hide the 'Add to cart' button on the front-end,
    //       but it will still be shown in the list; the back-end will
    //       prevent this department from being added to cart.
    // KB Aug 27, 2018: Deprecated. We will be able to set this on a per-sales context basis in the future.
    availableForPurchase: boolean;
    // NOTE: This applies to "ecommerce" context only. Will hide the entire department
    //       from the event list, but it will still be able to be added to cart if the
    //       user has the direct link.
    // KB Aug 27, 2018: Deprecated. We will be able to set this on a per-sales context basis in the future.
    showInEventList: boolean;

    // * Note that in the legacy system, the `available` flag is respected by box-office too,
    //   only kiosk mode (POS) ignores this. (Here we propose only "ecommerce" respects this)

    // KB Aug 27, 2018: Visibility determination should be made on the following going forward
    ecommerceVisibility: DepartmentVisibility;
    boxofficeVisibility: DepartmentVisibility;
    posVisibility: DepartmentVisibility;

    category: DepartmentCategory | null;

    sharedSecret?: string;
}

export interface DepartmentServiceFee {
    departmentServiceFeeId: number;
    serviceFeeId: number;
    name: string;
    value: number;
}

export interface DepartmentTimeRestriction {
    id: number;
    salesContext: SalesContext;
    from: number;
    durationMinutes: number;
    applicableDays: Array<string>;
}

export interface CompositeDepartment {
    id: number;
    name: string;
    details: string;
    extendedDetails: string | null;
    from: number;
    to: number;
    performanceDate: number | null;
    departmentIds: Array<number>;
}

export interface Client {
    id: number;
    name: string;
    authFee: number;
    cartUrl: string;
    currency: string;

    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;

    // Clients with this true will have transaction and auth fees waived.
    // This will be applied server-side (the fees will be filtered from the resulting cart,
    // and ignored on the resulting order)
    waiveFeesForOffline: boolean;
    waiveFeesForPos: boolean;

    posReceiptFooter?: string;

    allowCustomAmount: boolean;

    taxes: Array<ClientTax>;
    equivalentEMS2ClientIds: Array<number>;
    deliveryMethods: Array<DeliveryMethod>;
}

export interface ClientTax {
    id: number;
    value: number;
    name: string;
    active: boolean;
}

export interface VenueConfiguration {
    id: number;
    venue: Venue;
    name: string;
    assignedSeating: boolean;
}

export interface Venue {
    id: number;
    name: string;
    address: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
}

export interface DeliveryMethod {
    id: number;
    name: string;
    amount: number;
    isBoxofficeOnly: boolean;
}

export interface CustomField {
    id: number;
    name: string;
    internalName: string;
    type: string;
    required: boolean;
    boxofficeOnly: boolean;
    options: Array<CustomFieldOption>;
}

export interface CustomFieldOption {
    id: number;
    name: string;
    internalName: string;
    isOtherOption: boolean;
    boxofficeOnly: boolean;
}

export interface CustomerList {
    customers: Array<Customer>;
    hasMorePages: boolean;
    page: number;
    take: number;
}

export interface Customer {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    address2: string;
    city: string;
    postal: string;
    state: {
        id: number | null;
        name: string | null;
    };
    country: {
        id: number | null;
        name: string | null;
    };
}

export interface HoldGroupSectionRequest {
    id: number;
    departmentId: number;
    comp: boolean;
    quantity?: number | null;
    soldQuantity?: number | null;
    departments?: SimpleDepartment[];
    sectionDetails?: string;
    eventSeatmap?: string;
}

export interface HoldGroupSeatRequest {
    id: number;
    departmentId: number;
    comp: boolean;
    soldQuantity?: number | null;
    departments?: SimpleDepartment[];
    seatDetails?: string;
    eventSeatmap?: string;
}

export interface HoldGroupPackageRequest {
    id: number;
    priceGroupId: number;
    comp: boolean;
    // Required when the referenced HoldGroupEventPackage is section-scoped
    // (seat is null, section is set). Ignored for seat-scoped package holds.
    quantity?: number | null;
    soldQuantity?: number | null;
    priceGroups?: EventPackagePriceGroup[];
    packageName?: string;
    seatDetails?: string;
    sectionName?: string;
    maxQuantity?: number | null;
}

export interface HoldGroupTicketsRequest {
    sections: HoldGroupSectionRequest[];
    seats: HoldGroupSeatRequest[];
    packages: HoldGroupPackageRequest[];
}

export interface HoldGroupWrapper {
    holdGroups: HoldGroup[];
    seatmapPriceGroups: {
        id: number;
        name: string;
        color: string;
        eventSeatmapId: number;
        eventSeatmap: SimpleEventSeatmap;
        seatIds: number[];
        sectionIds: number[];
        departments: SimpleDepartment[];
    }[];
    packagePriceGroups: {
        id: number;
        name: string;
        priceGroups: EventPackagePriceGroup[];
    }[];
}

export enum SquareDeviceStatus {
    UNKNOWN = "UNKNOWN",
    UNPAIRED = "UNPAIRED",
    PAIRED = "PAIRED",
    EXPIRED = "EXPIRED",
}

export interface SimpleSquareDevice {
    id: number;
    isActive: boolean;
    dateCreated: string;
    pairByDate: string;
    deviceName: string;
    deviceCode: string;
    deviceId: string;
    status: SquareDeviceStatus;
}

export interface SquareDevice extends SimpleSquareDevice {
    squareLocationId: string;
    user: SimpleUser;
}

export function squareDeviceStatusFriendlyName(
    status: SquareDeviceStatus,
): string {
    switch (status) {
        case SquareDeviceStatus.UNPAIRED:
            return "Unpaired";
        case SquareDeviceStatus.PAIRED:
            return "Paired";
        case SquareDeviceStatus.EXPIRED:
            return "Expired";
        default:
            return "Unknown";
    }
}
